import { supabase } from "@/lib/supabase";

/**
 * A single record inside a dynamic section that can carry its own SEO override,
 * e.g. one experience, farm, farmer or story.
 */
export interface SectionItem {
  label: string;
  path: string;
  /** Short summary used to preview the {description} token with real data. */
  summary: string;
}

interface ItemSource {
  table: string;
  labelCol: string;
  slugCol: string | null;
  statusCol: string | null;
  /** Extra columns to select so `summaryFrom` can read them. */
  summaryCols: string[];
  /** Pulls a short summary string out of a row (empty string when unavailable). */
  summaryFrom: (row: Record<string, unknown>) => string;
}

const str = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/** farmers.bio is a jsonb array of strings; the first line reads as the intro. */
const firstBioLine = (value: unknown): string => {
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === "string" && entry.trim());
    return typeof first === "string" ? first.trim() : "";
  }
  return "";
};

/**
 * Which backend tables feed each section entry. The path's first segment is the
 * key (e.g. "/experiences" -> experiences table). Stories merge farmer stories
 * and blog posts, matching how the public journal is built.
 */
const SECTION_SOURCES: Record<string, ItemSource[]> = {
  "/experiences": [
    {
      table: "experiences",
      labelCol: "name",
      slugCol: "slug",
      statusCol: "status",
      summaryCols: ["tagline"],
      summaryFrom: (row) => str(row.tagline),
    },
  ],
  "/farms": [
    {
      table: "farms",
      labelCol: "name",
      slugCol: "slug",
      statusCol: null,
      summaryCols: ["summary"],
      summaryFrom: (row) => str(row.summary),
    },
  ],
  "/farmers": [
    {
      table: "farmers",
      labelCol: "name",
      slugCol: "slug",
      statusCol: null,
      summaryCols: ["bio", "village"],
      summaryFrom: (row) => firstBioLine(row.bio) || str(row.village),
    },
  ],
  "/stories": [
    {
      table: "farmer_stories",
      labelCol: "title",
      slugCol: null,
      statusCol: "status",
      summaryCols: ["excerpt"],
      summaryFrom: (row) => str(row.excerpt),
    },
    {
      table: "blog_posts",
      labelCol: "title",
      slugCol: "slug",
      statusCol: "status",
      summaryCols: ["excerpt"],
      summaryFrom: (row) => str(row.excerpt),
    },
  ],
};

/** Same slug rule the public journal uses for records without a stored slug. */
export function slugifyTitle(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** "/experiences/*" -> "/experiences" */
export function sectionRootFromWildcard(path: string): string {
  return path.replace(/\/\*$/, "");
}

/** "/experiences" -> "/experiences/*" */
export function sectionEntryPath(sectionRoot: string): string {
  return `${sectionRoot}/*`;
}

/** Whether we know how to list the records behind a section entry. */
export function hasSectionItems(sectionRoot: string): boolean {
  return Boolean(SECTION_SOURCES[sectionRoot]);
}

/**
 * How many exact-path overrides sit under a section, i.e. items that have their
 * own title/description instead of inheriting the section default.
 */
export function countSectionOverrides(
  rows: { path: string }[],
  sectionRoot: string,
): number {
  const prefix = `${sectionRoot}/`;
  return rows.filter((row) => !row.path.endsWith("/*") && row.path.startsWith(prefix)).length;
}

/**
 * Loads every record that can appear under a section, as { label, path, summary }
 * pairs. Unreadable sources are skipped rather than failing the whole panel.
 */
export async function fetchSectionItems(sectionRoot: string): Promise<SectionItem[]> {
  const sources = SECTION_SOURCES[sectionRoot];
  if (!sources) return [];

  const collected: SectionItem[] = [];

  for (const source of sources) {
    const columns = [
      "id",
      source.labelCol,
      ...(source.slugCol ? [source.slugCol] : []),
      ...(source.statusCol ? [source.statusCol] : []),
      ...source.summaryCols,
    ];
    try {
      const { data, error } = await supabase.from(source.table).select(columns.join(","));
      if (error) continue;
      ((data as Record<string, unknown>[] | null) || []).forEach((row) => {
        const label = String(row[source.labelCol] ?? "").trim();
        const slugValue = source.slugCol ? String(row[source.slugCol] ?? "").trim() : "";
        const slug = slugValue || slugifyTitle(label);
        if (!label || !slug) return;
        collected.push({
          label,
          path: `${sectionRoot}/${slug}`,
          summary: source.summaryFrom(row),
        });
      });
    } catch {
      // Skip this source; keep the panel usable.
    }
  }

  const byPath = new Map<string, SectionItem>();
  collected.forEach((item) => {
    if (!byPath.has(item.path)) byPath.set(item.path, item);
  });

  return [...byPath.values()].sort((a, b) => a.label.localeCompare(b.label));
}